// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import Row from '@tcp/core/src/components/common/atoms/Row';
import Col from '@tcp/core/src/components/common/atoms/Col';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import styles from '../styles/OrderSummaryDetails.style';
import formatAmount from '../utils';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
  getLabelWithDefaultValue,
} from '../../../../../../../utils/labels/labelUtilty';
import {
  ACCOUNT_ORDERS_LABEL,
  OrderDetailLabels,
  lblOrdersAdjustment,
  lblOrdersCouponsPromotions,
  lblOrdersFree,
  lblOrdersOrderSummary,
  lblOrdersOrderTotal,
  lblOrdersTax,
} from '../../../container/OrderDetails.labels';
import {
  GLOBAL_GENERIC_LABELS,
  GlobalGenericLabels,
} from '../../../../../../../utils/labels/globalGeneric.labels';

const getDiscountCouponAmount = (
  couponsTotal,
  adjustmentCouponTotal,
  isAppeasementEnabled,
  currencySymbol
) => {
  return isAppeasementEnabled
    ? formatAmount(adjustmentCouponTotal, currencySymbol)
    : formatAmount(couponsTotal, currencySymbol);
};

const taxComponentWithZeroOrderTotal = (
  grandTotal,
  totalTax,
  orderLabels,
  currencySymbol,
  lblOrdersFreeAmount
) => {
  return !grandTotal && totalTax ? (
    <Row fullBleed className="elem-mb-XS tax-component">
      <Col colSize={{ large: 7, medium: 4, small: 3 }}>
        <BodyCopy fontFamily="secondary" fontSize="fs14">
          {`${getLabelWithDefaultValue(orderLabels, lblOrdersTax)}:`}
        </BodyCopy>
      </Col>
      <Col colSize={{ large: 5, medium: 4, small: 3 }}>
        <BodyCopy fontFamily="secondary" fontSize="fs14" textAlign="right">
          {totalTax
            ? `${currencySymbol}${totalTax.toFixed(2)}`
            : `${currencySymbol}${lblOrdersFreeAmount}`}
        </BodyCopy>
      </Col>
    </Row>
  ) : null;
};

/**
 * This function component use for return the OrderShippingDetails
 * can be passed in the component.
 * @param ordersLabels - ordersLabels object used for showing Orders Labels
 */

const OrderSummaryDetails = ({ className, orderDetailsData, isAppeasementEnabled }) => {
  const { summary } = orderDetailsData || {};
  const {
    canceledItems,
    couponsTotal,
    currencySymbol,
    grandTotal,
    purchasedItems,
    shippingTotal,
    subTotal,
    totalTax,
    appeasementTotal,
    adjustmentCouponTotal,
  } = summary || {};

  const orderLabels = getLabelFromCopyOrFromTranslation(ACCOUNT_ORDERS_LABEL, OrderDetailLabels);
  const lblOrdersFreeAmount = getDynamicLabelValues('dynamic.account_d.lbl_orders_freeAmount');
  const { lbl_items: lblItems, lbl_shipping: lblShipping } = getLabelFromCopyOrFromTranslation(
    GLOBAL_GENERIC_LABELS,
    GlobalGenericLabels
  );
  /**
   * @function return  Used to render the JSX of the component
   * @param    {[Void]} function does not accept anything.
   * @return   {[Object]} JSX of the component
   */

  return (
    <BodyCopy component="div" className={className}>
      <Row fullBleed className="elem-mb-XS">
        <Col colSize={{ large: 7, medium: 4, small: 3 }}>
          <BodyCopy fontSize="fs14" fontWeight="extrabold" fontFamily="secondary">
            {getLabelWithDefaultValue(orderLabels, lblOrdersOrderSummary)}
          </BodyCopy>
        </Col>
        <Col colSize={{ large: 5, medium: 4, small: 3 }}>
          <BodyCopy fontSize="fs14" fontWeight="extrabold" fontFamily="secondary" textAlign="right">
            {getLabelWithDefaultValue(orderLabels, lblOrdersOrderTotal)}
          </BodyCopy>
        </Col>
      </Row>
      <Row fullBleed className="elem-mb-XS">
        <Col colSize={{ large: 7, medium: 4, small: 3 }}>
          <BodyCopy fontFamily="secondary" fontSize="fs14">
            {`${lblItems} (${purchasedItems || canceledItems}):`}
          </BodyCopy>
        </Col>
        <Col colSize={{ large: 5, medium: 4, small: 3 }}>
          <BodyCopy fontFamily="secondary" fontSize="fs14" textAlign="right">
            {formatAmount(subTotal, currencySymbol)}
          </BodyCopy>
        </Col>
      </Row>
      {!!grandTotal && (
        <>
          <Row fullBleed className="elem-mb-XS">
            <Col colSize={{ large: 7, medium: 4, small: 3 }}>
              <BodyCopy fontFamily="secondary" fontSize="fs14">
                {`${getLabelWithDefaultValue(orderLabels, lblOrdersCouponsPromotions)}:`}
              </BodyCopy>
            </Col>
            <Col colSize={{ large: 5, medium: 4, small: 3 }}>
              <BodyCopy fontFamily="secondary" fontSize="fs14" textAlign="right">
                {getDiscountCouponAmount(
                  couponsTotal,
                  adjustmentCouponTotal,
                  isAppeasementEnabled,
                  currencySymbol
                )}
              </BodyCopy>
            </Col>
          </Row>

          <Row fullBleed className="elem-mb-XS">
            <Col colSize={{ large: 7, medium: 4, small: 3 }}>
              <BodyCopy fontFamily="secondary" fontSize="fs14">
                {`${lblShipping}:`}
              </BodyCopy>
            </Col>
            <Col colSize={{ large: 5, medium: 4, small: 3 }}>
              <BodyCopy fontFamily="secondary" fontSize="fs14" textAlign="right">
                {shippingTotal > 0
                  ? formatAmount(shippingTotal, currencySymbol)
                  : `${getLabelWithDefaultValue(orderLabels, lblOrdersFree)}`}
              </BodyCopy>
            </Col>
          </Row>

          <Row fullBleed className="elem-mb-XS">
            <Col colSize={{ large: 7, medium: 4, small: 3 }}>
              <BodyCopy fontFamily="secondary" fontSize="fs14">
                {`${getLabelWithDefaultValue(orderLabels, lblOrdersTax)}:`}
              </BodyCopy>
            </Col>
            <Col colSize={{ large: 5, medium: 4, small: 3 }}>
              <BodyCopy fontFamily="secondary" fontSize="fs14" textAlign="right">
                {totalTax
                  ? `${currencySymbol}${totalTax.toFixed(2)}`
                  : `${currencySymbol}${lblOrdersFreeAmount}`}
              </BodyCopy>
            </Col>
          </Row>
        </>
      )}
      {taxComponentWithZeroOrderTotal(
        grandTotal,
        totalTax,
        orderLabels,
        currencySymbol,
        lblOrdersFreeAmount
      )}
      <Row fullBleed className="elem-mb-XS total-value">
        <Col colSize={{ large: 7, medium: 4, small: 3 }}>
          <BodyCopy fontSize="fs16" fontWeight="extrabold" fontFamily="secondary">
            {`${getLabelWithDefaultValue(orderLabels, lblOrdersOrderTotal)}:`}
          </BodyCopy>
        </Col>
        <Col colSize={{ large: 5, medium: 4, small: 3 }}>
          <BodyCopy fontSize="fs16" fontWeight="extrabold" fontFamily="secondary" textAlign="right">
            {grandTotal
              ? `${currencySymbol}${grandTotal.toFixed(2)}`
              : `${currencySymbol}${lblOrdersFreeAmount}`}
          </BodyCopy>
        </Col>
      </Row>
      {appeasementTotal && isAppeasementEnabled ? (
        <Row fullBleed className="elem-mb-XS">
          <Col colSize={{ large: 7, medium: 4, small: 3 }}>
            <BodyCopy fontFamily="secondary" fontSize="fs14">
              {`${getLabelWithDefaultValue(orderLabels, lblOrdersAdjustment)}: `}
            </BodyCopy>
          </Col>
          <Col colSize={{ large: 5, medium: 4, small: 3 }}>
            <BodyCopy fontFamily="secondary" fontSize="fs14" textAlign="right">
              {formatAmount(appeasementTotal, currencySymbol)}
            </BodyCopy>
          </Col>
        </Row>
      ) : null}
    </BodyCopy>
  );
};
OrderSummaryDetails.propTypes = {
  className: PropTypes.string,
  orderDetailsData: PropTypes.shape({}),
  isAppeasementEnabled: PropTypes.bool,
};

OrderSummaryDetails.defaultProps = {
  className: '',
  orderDetailsData: {},
  isAppeasementEnabled: false,
};

export default withStyles(OrderSummaryDetails, styles);
export { OrderSummaryDetails as OrderSummaryDetailsVanilla };
